import React, { useEffect, useMemo, useRef, useState } from 'react';
import './index.css';
import firstImage from '../../images/full-logo.png';
import { Link } from 'gatsby';
import Helmet from 'react-helmet';
import Recaptcha from 'react-google-recaptcha';

const RECAPTCHA_KEY = '6LepaFQiAAAAADYtSZwFEJMwV1VU30iJrm9pIZ8Z';

const steps = [
  {
    id: 'focus',
    title: 'What are you building?',
    subtitle: 'Choose the focus that best matches your project.',
    type: 'options',
    options: [
      { value: 'Product strategy + MVP', helper: 'Validate and ship fast.' },
      { value: 'Web application', helper: 'Modern web platforms and dashboards.' },
      { value: 'Mobile application', helper: 'iOS + Android delivery.' },
      { value: 'Design system', helper: 'UI foundations and UX cohesion.' },
      { value: 'Team extension', helper: 'Embed with your team.' },
      { value: 'Product rescue', helper: 'Fix performance, stability, or UX.' },
    ],
  },
  {
    id: 'model',
    title: 'How should we engage?',
    subtitle: 'Select the collaboration model that works best.',
    type: 'options',
    options: [
      { value: 'Dedicated team', helper: 'Longer-term delivery and ownership.' },
      { value: 'Project-based', helper: 'Fixed scope with clear milestones.' },
      { value: 'Prototype sprint', helper: '2–4 week proof of concept.' },
      { value: 'Advisory / audit', helper: 'Short, high-impact guidance.' },
    ],
  },
  {
    id: 'timeline',
    title: 'Timeline expectations?',
    subtitle: 'When do you want to start and see momentum?',
    type: 'options',
    options: [
      { value: 'ASAP (0–1 month)', helper: 'Immediate start.' },
      { value: '1–3 months', helper: 'Planned start with prep.' },
      { value: '3–6 months', helper: 'Roadmap aligned.' },
      { value: '6+ months', helper: 'Exploratory or R&D.' },
    ],
  },
  {
    id: 'budget',
    title: 'Budget range',
    subtitle: 'Helps us tailor scope and the right team size.',
    type: 'options',
    options: [
      { value: 'Under $10k', helper: 'Lightweight sprint or audit.' },
      { value: '$10k–$25k', helper: 'Focused build or design.' },
      { value: '$25k–$50k', helper: 'MVP or multi-feature release.' },
      { value: '$50k+', helper: 'Full product buildout.' },
    ],
  },
  {
    id: 'contact',
    title: 'Tell us about the project',
    subtitle: 'Share context so we can respond quickly.',
    type: 'contact',
  },
];

const HireADevPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({
    focus: '',
    model: '',
    timeline: '',
    budget: '',
    name: '',
    email: '',
    company: '',
    details: '',
    captcha: '',
  });
  const stepTopRef = useRef(null);
  const pageTopRef = useRef(null);
  const recaptchaRef = useRef(null);

  const currentStep = steps[currentIndex];
  const isLastStep = currentIndex === steps.length - 1;
  const progress = Math.round(((currentIndex + 1) / steps.length) * 100);

  const isStepValid = useMemo(() => {
    if (currentStep.type === 'options') {
      return Boolean(answers[currentStep.id]);
    }
    if (currentStep.type === 'contact') {
      return Boolean(answers.name && answers.email && answers.details && answers.captcha);
    }
    return true;
  }, [answers, currentStep]);

  const updateAnswer = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const handleNext = () => {
    if (!isStepValid) return;
    if (!isLastStep) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex === 0) return;
    setCurrentIndex(prev => prev - 1);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const scrollToTop = () => {
      if (pageTopRef.current) {
        pageTopRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
      window.scrollTo({ top: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    scrollToTop();
    const raf = window.requestAnimationFrame(scrollToTop);
    const timeout = window.setTimeout(scrollToTop, 200);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
    };
  }, [currentIndex]);

  return (
    <div>
      <Helmet>
        <html lang="en" />
        <title>Start a project | Coetic X</title>
        <meta
          name="description"
          content="Start a project with Coetic X. Share your needs in a few steps and we will follow up quickly."
        />
        <meta property="og:type" content="business.business" />
        <meta property="og:title" content="Start a project" />
        <meta property="og:url" content="/start-a-project" />
        <meta name="google-site-verification" content="TAZyvCwfNRod7T0FvgzEaEznjHHjLWds9xo3qjgcoOQ" />
      </Helmet>

      <h1 style={{ display: 'none' }}>Start a project</h1>

      <form method="post" netlify={true} action="/.netlify/functions/services" className="hire-form">
        <div ref={pageTopRef} />
        <input type="hidden" name="projectFocus" value={answers.focus} />
        <input type="hidden" name="engagementModel" value={answers.model} />
        <input type="hidden" name="timeline" value={answers.timeline} />
        <input type="hidden" name="budgetRange" value={answers.budget} />

        <div className="hire-flow">
          <div className="hire-flow__inner">
            <header className="hire-flow__header">
              <Link className="coetic-x-logo" to="/">
                <img className="coetic-x" src={firstImage} alt="coetic-x logo" />
              </Link>
              <div className="hire-flow__progress">
                <span>Step {currentIndex + 1} of {steps.length}</span>
                <div className="progress-bar">
                  <span style={{ width: `${progress}%` }} />
                </div>
              </div>
            </header>

            <div className="hire-step" key={currentStep.id} ref={stepTopRef}>
              <div className="hire-step__intro">
                <p className="hire-eyebrow">Start a project</p>
                <h2 className="hire-step__title">{currentStep.title}</h2>
                <p className="hire-step__subtitle">{currentStep.subtitle}</p>
              </div>

              <div className="hire-step__body">
                {currentStep.type === 'options' && (
                  <div className="hire-options">
                    {currentStep.options.map(option => (
                      <button
                        type="button"
                        className={`hire-option ${answers[currentStep.id] === option.value ? 'is-active' : ''}`}
                        key={option.value}
                        onClick={() => updateAnswer(currentStep.id, option.value)}
                      >
                        <span className="hire-option__label">{option.value}</span>
                        <span className="hire-option__helper">{option.helper}</span>
                      </button>
                    ))}
                  </div>
                )}

                {currentStep.type === 'contact' && (
                  <div className="hire-contact">
                    <label className="hire-field">
                      <span>Your name</span>
                      <input
                        className="hire-input"
                        type="text"
                        name="name"
                        value={answers.name}
                        onChange={e => updateAnswer('name', e.target.value)}
                        placeholder="First and last name"
                        required
                      />
                    </label>
                    <label className="hire-field">
                      <span>Email</span>
                      <input
                        className="hire-input"
                        type="email"
                        name="email"
                        value={answers.email}
                        onChange={e => updateAnswer('email', e.target.value)}
                        placeholder="you@company.com"
                        required
                      />
                    </label>
                    <label className="hire-field">
                      <span>Company (optional)</span>
                      <input
                        className="hire-input"
                        type="text"
                        name="company"
                        value={answers.company}
                        onChange={e => updateAnswer('company', e.target.value)}
                        placeholder="Company or product"
                      />
                    </label>
                    <label className="hire-field">
                      <span>Project details</span>
                      <textarea
                        className="hire-input hire-textarea"
                        name="details"
                        value={answers.details}
                        onChange={e => updateAnswer('details', e.target.value)}
                        placeholder="Share goals, scope, or challenges."
                        required
                      />
                    </label>
                    <p className="hire-hint">We typically respond within 24 hours.</p>
                    <div className="hire-recaptcha">
                      <Recaptcha
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_KEY}
                        onChange={token => updateAnswer('captcha', token || '')}
                        onExpired={() => updateAnswer('captcha', '')}
                        onErrored={() => updateAnswer('captcha', '')}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <footer className="hire-flow__footer">
              <div className="hire-footer__left">
                <button
                  type="button"
                  className="hire-back"
                  onClick={handleBack}
                  disabled={currentIndex === 0}
                >
                  Back
                </button>
                <button
                  type={isLastStep ? 'submit' : 'button'}
                  className="hire-next"
                  onClick={handleNext}
                  disabled={!isStepValid}
                >
                  {isLastStep ? 'Send request' : 'Continue'}
                </button>
              </div>
            </footer>
          </div>
        </div>
      </form>
    </div>
  );
};

export default HireADevPage;
